import { useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import type { CustomerRatingsOverviewFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerRatingsOverviewFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { customerRatingsOverviewMessages } from './CustomerRatingsOverview.messages';
import { CustomerRatingIconsWithReviewCount } from '../CustomerRatingIconsWithReviewCount/CustomerRatingIconsWithReviewCount';
import { CustomerRatingsCount } from '../CustomerRatingsCount/CustomerRatingsCount';

/* GraphQL */ `
  fragment CustomerRatingsOverviewFragment on RatingProduct {
    avgRating
    reviewCount
    reviewStatistics {
      count
      portion
      rating
    }
  }
`;

export type CustomerRatingsOverviewProps = {
  data: FragmentType<typeof CustomerRatingsOverviewFragmentFragmentDoc>;
};

/**
 * The CustomerRatingsOverview component displays the rating icons with the amount of given ratings
 * and a visual statistic of how many ratings are available for a specific rating
 */
export const CustomerRatingsOverview = ({ data }: CustomerRatingsOverviewProps) => {
  const { avgRating, reviewCount, reviewStatistics } =
    unmask<typeof CustomerRatingsOverviewFragmentFragmentDoc>(data);
  const { formatMessage } = useIntl();

  return (
    <div>
      <CustomerRatingIconsWithReviewCount ratingValue={avgRating} ratingsCount={reviewCount} />
      <div className="mt-2 grid grid-cols-[auto_1fr_auto] gap-0.5">
        {reviewStatistics.map((ratingStatistic, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="col-span-3 grid grid-cols-subgrid items-center gap-1.5" key={index}>
            <div className="text-size-body3">
              {formatMessage(customerRatingsOverviewMessages.label, {
                rating: ratingStatistic.rating,
              })}
            </div>
            <div>
              <figure
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={ratingStatistic.portion}
                className="bg-color-grey-light overflow-hidden rounded-[4px]"
              >
                <div
                  className="bg-color-grey-dark h-[12px]"
                  style={{ width: `${ratingStatistic.portion}%` }}
                />
              </figure>
            </div>
            <div>
              <CustomerRatingsCount ratingsCount={ratingStatistic.count} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
